import { DeleteOutline } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";

import {
  pickNotDeprecated,
  useCan,
  useDelete,
  useMutationMode,
  useResource,
  useTranslate,
  useWarnAboutChange
} from "@refinedev/core";
import { DeleteButtonProps } from "@refinedev/mui";
import { config } from "config";
import React, { useEffect, useState } from "react";
import { dataProvider } from "../../rest-data-provider";

export function isValidKey(
  key: string | number | symbol,
  object: object
): key is keyof typeof object {
  return key in object;
}
interface IObject {
  name?: string;
  number?: number;
}
type IRelatedDataArr = IObject[];

export function DialogTable(props: any) {
  const apiUrl = config().server.api;
  const [relatedDate, setRelatedDate] = useState<IRelatedDataArr>();
  useEffect(() => {
    if (props.resource?.name === "user" && props.id !== undefined) {
      dataProvider(apiUrl)
        .getOne({ resource: "user-related-data", id: props.id })
        .then((res) => {
          const relatedDate = Object.keys(res.data)
            .filter((item) => item !== "users")
            .map((item) => {
              if (isValidKey(item, res.data)) {
                return {
                  name: item,
                  number: res.data[item].length,
                };
              }
              return {};
            });
          setRelatedDate(relatedDate);
        });
    }
  }, [apiUrl, props, props.recordItemId, props.resource?.name]);
  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 500 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell align="right" width={50}>Number</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {relatedDate?.map((row) => {
            return (
              <TableRow key={row.name}>
                <TableCell>{row.name}</TableCell>
                <TableCell align="right">{row.number}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export const DeleteButton: React.FC<DeleteButtonProps> = ({
  resource: resourceNameFromProps,
  resourceNameOrRouteName,
  recordItemId,
  onSuccess,
  mutationMode: mutationModeProp,
  children,
  successNotification,
  errorNotification,
  hideText = false,
  accessControl,
  meta,
  metaData,
  dataProviderName,
  confirmTitle,
  confirmOkText,
  confirmCancelText,
  svgIconProps,
  invalidates,
  ...rest
}) => {
  const accessControlEnabled = accessControl?.enabled ?? true;
  const hideIfUnauthorized = accessControl?.hideIfUnauthorized ?? false;
  const translate = useTranslate();
  const { id, resource } = useResource(
    resourceNameFromProps ?? resourceNameOrRouteName
  );
  const { mutationMode: mutationModeContext } = useMutationMode();

  const mutationMode = mutationModeProp ?? mutationModeContext;

  const { mutate, isLoading, variables } = useDelete();

  const { data } = useCan({
    resource: resource?.name,
    action: "delete",
    params: { id: recordItemId ?? id, resource },
    queryOptions: {
      enabled: accessControlEnabled,
    },
  });
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseOnConfirm = () => {
    if ((recordItemId ?? id) && resource?.name) {
      setWarnWhen(false);
      setOpen(false);
      mutate(
        {
          id: recordItemId ?? id ?? "",
          resource: resource?.name,
          mutationMode,
          successNotification,
          errorNotification,
          meta: pickNotDeprecated(meta, metaData),
          metaData: pickNotDeprecated(meta, metaData),
          dataProviderName,
          invalidates,
        },
        {
          onSuccess: (value) => {
            onSuccess && onSuccess(value);
          },
        }
      );
    }
  };

  const { sx, ...restProps } = rest;

  const { setWarnWhen } = useWarnAboutChange();

  if (accessControlEnabled && hideIfUnauthorized && !data?.can) {
    return null;
  }

  return (
    <div>
      <LoadingButton
        color="error"
        onClick={handleClickOpen}
        disabled={data?.can === false}
        loading={(recordItemId ?? id) === variables?.id && isLoading}
        startIcon={!hideText && <DeleteOutline {...svgIconProps} />}
        sx={{ minWidth: 0, ...sx }}
        loadingPosition={hideText ? "center" : "start"}
        {...restProps}
      >
        {hideText ? (
          <DeleteOutline fontSize="small" {...svgIconProps} />
        ) : (
          children ?? translate("buttons.delete", "Delete")
        )}
      </LoadingButton>
      {resource?.name === "user" ? (
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            This operation will delete the user's all related data, are you sure
            to continue? Related data as follows:
          </DialogTitle>
          <DialogContent>
            <DialogTable resource={resource} id={recordItemId} />
          </DialogContent>
          <DialogActions sx={{ justifyContent: "right" }}>
            <Button onClick={handleClose}>
              {confirmCancelText ?? translate("buttons.cancel", "Cancel")}
            </Button>
            <Button color="error" onClick={handleCloseOnConfirm} autoFocus>
              {confirmOkText ?? translate("buttons.delete", "Delete")}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {confirmTitle ?? translate("buttons.confirm", "Are you sure?")}
          </DialogTitle>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button onClick={handleClose}>
              {confirmCancelText ?? translate("buttons.cancel", "Cancel")}
            </Button>
            <Button color="error" onClick={handleCloseOnConfirm} autoFocus>
              {confirmOkText ?? translate("buttons.delete", "Delete")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};
